import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useStudio } from '../../hooks/useStudio';
import { useFetchStudioBooking } from '../../hooks/useFetchStudioBooking.js';
import { BookingProvider } from '../../store/bookingStore';
import MessageSender from '../../components/MessageSender.jsx';
import { NavBarContext } from '../../App';
import { buildCoarseLocationText } from '../../util/locationPrivacy';

import classes from './StudioMessagePage.module.css';

const StudioMessagePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: userId } = useSelector((state) => state?.auth?.user);
  const { showSignIn, showRegister } = useContext(NavBarContext);

  const { studio: data, loading, notFound } = useStudio(id);
  const { bookingStudioData, loading: isBookingLoading, error: bookingError } = useFetchStudioBooking(id);

  const isLoading = loading || isBookingLoading;

  const handleClose = () => {
    // Navigate back to the studio page
    navigate(`/studio/${id}`);
  };

  if (notFound) {
    return (
      <div className={classes.errorContainer}>
        <h2>Studio nicht gefunden</h2>
        <button onClick={() => navigate('/')} className={classes.backButton}>
          Zur Startseite
        </button>
      </div>
    );
  }

  if (isLoading || isBookingLoading) {
    return (
      <div className={classes.container}>
        {/* Header skeleton */}
        <header className={classes.loadingHeader}>
          <div className={classes.loadingHeaderContainer}>
            <div className={classes.loadingLogoContainer}>
              <div className={classes.loadingLogo}></div>
              <div className={classes.loadingNavSpans}>
                <div className={classes.loadingNavSpan}></div>
                <div className={classes.loadingNavSpan}></div>
              </div>
            </div>
            <div className={classes.loadingUserContainer}>
              <div className={classes.loadingUserImage}></div>
            </div>
          </div>
        </header>

        {/* Main content with skeleton */}
        <div className={classes.loadingMainContent}>
          <div className={classes.loadingMessageContainer}>
            <div className={classes.loadingMessagesHeader}>
              <div className={classes.loadingUserProfile}></div>
              <div className={classes.loadingTitle}></div>
            </div>
            
            <div className={classes.loadingStudioContainer}>
              <div className={classes.loadingStudioPhoto}></div>
              <div className={classes.loadingStudioInfo}>
                <div className={classes.loadingStudioName}></div>
                <div className={classes.loadingStudioLocation}></div>
              </div>
            </div>

            <div className={classes.loadingInfoBox}></div>
            
            <div className={classes.loadingDateTimeContainer}>
              <div className={classes.loadingDateTimeHeader}>
                <div className={classes.loadingDateTimeTitle}></div>
                <div className={classes.loadingSwitch}></div>
              </div>
            </div>

            <div className={classes.loadingMessageInputContainer}>
              <div className={classes.loadingMessageTitle}></div>
              <div className={classes.loadingMessageInput}></div>
            </div>
          </div>

          {/* Centered loading spinner */}
          <div className={classes.loadingSpinnerContainer}>
            <div className={classes.loadingSpinner}></div>
          </div>
        </div>

        {/* Footer skeleton */}
        <footer className={classes.loadingFooter}>
          <div className={classes.loadingBackButton}></div>
          <div className={classes.loadingSendButton}></div>
        </footer>
      </div>
    );
  }

  return (
    <BookingProvider studioId={id}>
      <div className={`${classes.container} ${showSignIn || showRegister ? classes.width : ''}`}>
        <MessageSender
          studioPricePerHour={data?.pricePerHour}
          userId={userId}
          studioUserFirstName={data?.User?.firstName}
          minHours={data?.minNumberOfHours}
          studioUserId={data?.User?.id}
          studioUserProfilePicture={data?.User?.processedProfilePicture}
          studioId={data?.id}
          studioName={data?.StudioName}
          studioImage={data?.processedImages?.[0]}
          rating={data?.averageRating}
          city={buildCoarseLocationText(data)}
          onClose={handleClose}
          bookingStudioData={bookingStudioData}
        />
      </div>
    </BookingProvider>
  );
};

export default StudioMessagePage;
